import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Grid,
  Stack,
  Button,
  Accordion,
  Typography,
  CircularProgress,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import authService from 'src/services/auth.service';
import { genericApiToaster } from 'src/services/utils';
import { titleHeadingStyle, titleHeadingStyleSmole } from 'src/pages/style';

import ActionButton from 'src/components/action-button/action-button';

import filterByTab from './filterByTab';
import TicketsTable from './table/table';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import ModifyTicket from './modals/ModifyTicket';
import AdvancedFilter from './table/advancedFilter';
import { getComparator } from '../../utils/filterData';
import { fetchStatuses } from '../../services/status.service';
import { fetchTicketsByDate } from '../../services/ticket.service';
import { fetchTagsByDepartment } from '../../services/tag.service';
import { fetchDepartments } from '../../services/department.service';
import DashboardToolbar from '../../components/ToolBars/DashboardToolbar';

TicketTab.propTypes = { title: PropTypes.string.isRequired };

const departmentCache = { data: null };

export default function TicketTab({ title }) {
  const { id: currentUserId, departmentIds, permissions } = useAuth();
  const { showToast, hideToast } = useToast();
  const { t } = useTranslation(['translation', 'tickettt']);
  const { isSmallScreen } = useResponsiveScreen();
  const navigate = useNavigate();

  const [ticketsData, setTicketsData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);

  const [statusesList, setStatusesList] = useState([]);
  const [filterByData, setFilterByData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openModifyTicket, setOpenModifyTicket] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [dataInCSV, setDataInCSV] = useState('');

  // The 4 separated state variables for independent searching and filtering
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [filterBy, setFilterBy] = useState('');

  // Metadata states loaded for the filter popovers
  const [departmentsData, setDepartmentsData] = useState([]);
  const [tagsData, setTagsData] = useState([]);

  // Title filter states for table binding
  const [filterTitle, setFilterTitle] = useState('');

  const hasCreatePermission = permissions?.includes('TICKET:CREATE');
  const hasDownloadPermission = permissions?.includes('CSV:DOWNLOAD');

  const titleMap = {
    ALL_TICKETS: 'allTickets',
    MY_TICKETS: 'myTickets',
    ASSIGNED_TO_ME: 'assignedToMe',
    TAGGED_TICKETS: 'taggedTickets',
  };

  const startRaw = secureLocalStorage.getItem('ticketStart');
  const endRaw = secureLocalStorage.getItem('ticketEnd');

  const safeStart = useMemo(() => {
    const d = new Date(startRaw);
    return Number.isNaN(d.getTime()) ? new Date(new Date().getFullYear(), 0, 1) : d;
  }, [startRaw]);

  const safeEnd = useMemo(() => {
    const d = new Date(endRaw);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [endRaw]);

  // Fetch all departments once on mount, caching them across all tab switches
  useEffect(() => {
    if (!departmentCache.data) {
      fetchDepartments()
        .then((res) => {
          departmentCache.data = res || [];
        })
        .catch((err) =>
          console.error('Failed to load departments list inside tab:', err)
        );
    }
  }, []);

  const fetchTickets = useCallback(
    async (sDate, eDate) => {
      setIsLoading(true);
      const dateA = new Date(sDate || safeStart);
      const dateB = new Date(eDate || safeEnd);

      const finalStart = Number.isNaN(dateA.getTime()) ? safeStart : dateA;
      const finalEnd = Number.isNaN(dateB.getTime()) ? safeEnd : dateB;

      await genericApiToaster(
        fetchTicketsByDate,
        [
          {
            startDate: finalStart.toISOString(),
            endDate: finalEnd.toISOString(),
            attributeName: 'created',
            departmentIds,
          },
        ],
        '',
        t('errorMessages.couldntLoadTicket'),
        showToast,
        hideToast,
        t,
        (res) => {
          setTicketsData(res || []);
          setPage(0);
        },
        undefined,
        () => setIsLoading(false)
      );
    },
    [safeStart, safeEnd, t, showToast, hideToast, departmentIds]
  );

  useEffect(() => {
    const sync = async () => {
      try {
        const stats = await fetchStatuses();
        setStatusesList((stats || []).filter((s) => s.name !== 'Hidden'));

        // Dynamically loads the active department attributes to populate the sub-popper
        if (departmentIds && departmentIds.length > 0) {
          const tagsList = await fetchTagsByDepartment(departmentIds[0]);
          setTagsData(tagsList || []);
        }

        const depts = await fetchDepartments();
        setDepartmentsData(depts || []);
        setIsMetadataLoaded(true);
      } catch (e) {
        setIsMetadataLoaded(true);
      }
    };
    sync();
    fetchTickets();
  }, [fetchTickets, departmentIds]);

  const ticketsWithStatus = useMemo(() => {
    if (!isMetadataLoaded || !ticketsData) return [];
    const statusMap = Object.fromEntries(statusesList.map((s) => [s.statusId, s]));
    return ticketsData.map((tk) => ({
      ...tk,
      statusObject: statusMap[tk.status_id] || null,
      status: statusMap[tk.status_id]?.name || '',
    }));
  }, [ticketsData, statusesList, isMetadataLoaded]);

  const updateItemsWithStatus = useCallback(async () => {
    if (!isMetadataLoaded || ticketsWithStatus.length === 0) return;
    await filterByTab(
      title,
      ticketsWithStatus,
      currentUserId,
      authService.verifyUserRole(),
      departmentIds,
      () => {},
      setFilterByData,
      showToast,
      hideToast,
      t
    );
  }, [
    ticketsWithStatus,
    isMetadataLoaded,
    title,
    currentUserId,
    departmentIds,
    t,
    showToast,
    hideToast,
  ]);

  useEffect(() => {
    updateItemsWithStatus();
  }, [updateItemsWithStatus]);

  const filteredTickets = useMemo(() => {
    let result = filterByData;

    // First: Apply categorical filtering (Status, Department, Priority, Tag)
    if (filterBy && filterTitle) {
      // FIXED: Check filterTitle directly
      const targetVal = filterTitle.toLowerCase().trim();
      result = result.filter((tk) => {
        if (filterBy === 'status') {
          if (!filterTitle.includes('|')) {
            return tk.statusObject?.name?.toLowerCase() === targetVal;
          }
          const [statusName, deptName] = filterTitle.split('|');
          return (
            tk.statusObject?.name?.toLowerCase() === statusName.toLowerCase().trim() &&
            tk.department?.toLowerCase() === deptName.toLowerCase().trim()
          );
        }
        if (filterBy === 'department') {
          return tk.department?.toLowerCase() === targetVal;
        }
        if (filterBy === 'priority') {
          return tk.priority?.toLowerCase() === targetVal;
        }
        if (filterBy === 'tag') {
          const tkTags = tk.tags || [];
          return tkTags.some((tagObj) => {
            const nameVal = tagObj?.tag?.name || tagObj?.name || '';
            return nameVal.toLowerCase() === targetVal;
          });
        }
        return true;
      });
    }

    // Second: Apply text query search (ID, Title, Category)
    if (searchValue) {
      const query = searchValue.toLowerCase().trim();
      result = result.filter((tk) => {
        if (searchBy === 'id') {
          return String(tk.id).includes(query);
        }
        if (searchBy === 'title') {
          return tk.title && tk.title.toLowerCase().includes(query);
        }
        if (searchBy === 'category') {
          return tk.category && tk.category.toLowerCase().includes(query);
        }

        // Fallback generic search
        const fieldVal = tk[searchBy];
        return fieldVal && String(fieldVal).toLowerCase().includes(query);
      });
    }

    // Third: Sort the final filtered array using the template's comparator
    const comparator = getComparator(order, orderBy);
    const stabilizedThis = result.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderCompare = comparator(a[0], b[0]);
      if (orderCompare !== 0) return orderCompare;
      return a[1] - b[1];
    });

    return stabilizedThis.map((el) => el[0]);
    // FIXED: Added filterTitle as a dependency and removed unused filterValue
  }, [searchValue, order, orderBy, searchBy, filterBy, filterTitle, filterByData]);

  useEffect(() => {
    if (filteredTickets.length > 0) {
      const sanitizeCsvField = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value).trim();
        if (
          str.startsWith('=') ||
          str.startsWith('+') ||
          str.startsWith('-') ||
          str.startsWith('@')
        ) {
          return `'${str}`; // Prepends a single quote to force text interpretation
        }
        return str;
      };

      const headers = 'ID,Title,Department,Priority,Status,Created At\n';
      const rows = filteredTickets
        .map(
          (tk) =>
            `"${sanitizeCsvField(tk.id)}","${sanitizeCsvField(
              tk.title
            )}","${sanitizeCsvField(tk.department)}","${sanitizeCsvField(
              tk.priority
            )}","${sanitizeCsvField(tk.status)}","${sanitizeCsvField(tk.createdAt)}"`
        )
        .join('\n');
      setDataInCSV(headers + rows);
    } else {
      setDataInCSV('');
    }
  }, [filteredTickets]);

  if (!isMetadataLoaded)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography
          variant="h4"
          style={isSmallScreen ? titleHeadingStyleSmole : titleHeadingStyle}
        >
          {t(`ticketTabs.${titleMap[title] || 'allTickets'}`)}
        </Typography>

        <Grid>
          <Stack spacing={1.5} alignItems="flex-end">
            <Stack direction="row" spacing={1}>
              {hasCreatePermission && (
                <ActionButton
                  onClick={() => navigate('/dashboard/newTicket')}
                  icon="eva:plus-fill"
                  text={t('buttons.newTicket')}
                />
              )}
              {hasDownloadPermission && (
                <ActionButton
                  icon="material-symbols:download"
                  text={t('buttons.download')}
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(dataInCSV)}`}
                  download="tickets.csv"
                />
              )}
            </Stack>
          </Stack>
        </Grid>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <DashboardToolbar
          getTicketsByDate={fetchTickets}
          start={safeStart}
          end={safeEnd}
        />
        <Button
          variant={isAdvancedFilterOpen ? 'contained' : 'outlined'}
          onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
          sx={{ height: '37px', borderRadius: '12px', fontWeight: 900 }}
        >
          {t('buttons.advancedFilter')}
        </Button>
      </Box>

      <Accordion
        expanded={isAdvancedFilterOpen}
        sx={{
          bgcolor: 'transparent',
          boxShadow: 'none',
          mb: 3,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary sx={{ display: 'none' }} />
        <AccordionDetails sx={{ p: 0 }}>
          <AdvancedFilter
            ticketsWithStatus={ticketsWithStatus}
            setFilterByData={setFilterByData}
            onClose={() => setIsAdvancedFilterOpen(false)}
          />
        </AccordionDetails>
      </Accordion>

      <TicketsTable
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        // FIXED: Passes state matches directly to avoid any type/function crashes
        filterTitle={filterTitle}
        setFilterTitle={setFilterTitle}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        isLoading={isLoading}
        statusesList={statusesList}
        setSelectedItem={setSelectedItem}
        setOpenModifyTicket={setOpenModifyTicket}
        getTicketsByDateRange={() => fetchTickets()}
        ticketsWithStatus={filterByData}
        setFilterByData={setFilterByData}
        currentPageTickets={filteredTickets.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )}
        filteredTickets={filteredTickets}
        selectedItem={selectedItem}
        departmentData={departmentsData}
        tagsData={tagsData}
        filterBy={filterBy}
        setFilterBy={setFilterBy}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />
      {openModifyTicket && (
        <ModifyTicket
          open={openModifyTicket}
          setOpen={setOpenModifyTicket}
          row={selectedItem}
          statusesList={statusesList}
          fetchTicketData={() => fetchTickets()}
        />
      )}
    </Box>
  );
}
